<!-- src/pages/Sign.vue -->
<script setup>
import { onMounted, onBeforeUnmount, ref, nextTick } from 'vue'
import SignaturePad from 'signature_pad'
import { api } from '../api/client'
import { useRouter } from 'vue-router'

const router = useRouter()

const htmlPreview = ref('<div class="note">Đang tải hợp đồng…</div>')
const isSubmitting = ref(false)

// DOM (trong v-html)
let sig = null
let canvasEl = null
let agree1El = null
let agree2El = null
let ro = null
let resizeRAF = 0
let lastStrokes = null

async function fetchPreview () {
  const { data } = await api.get('/ctv/contract/preview')
  htmlPreview.value = data?.html || '<div class="error">Không tải được mẫu hợp đồng</div>'
}

function sizeCanvas (c) {
  const ratio = Math.max(window.devicePixelRatio || 1, 1)
  const rect = c.getBoundingClientRect()
  const w = Math.max(1, Math.floor(rect.width))
  const h = Math.max(1, Math.floor(rect.height))
  c.width  = Math.floor(w * ratio)
  c.height = Math.floor(h * ratio)
  const ctx = c.getContext('2d')
  ctx.setTransform(1,0,0,1,0,0)
  ctx.scale(ratio, ratio)
}

function initSignatureOn (canvas) {
  if (!canvas) return
  if (sig && !sig.isEmpty()) { try { lastStrokes = sig.toData() } catch { lastStrokes = null } }
  sizeCanvas(canvas)
  if (!sig) {
    sig = new SignaturePad(canvas, { penColor:'#111827', backgroundColor:'rgba(0,0,0,0)', minWidth:0.6, maxWidth:2.2, throttle:4 })
  } else {
    sig.clear()
  }
  if (lastStrokes && Array.isArray(lastStrokes) && lastStrokes.length) { try { sig.fromData(lastStrokes) } catch {} }
}

async function hydrateTemplate () {
  await nextTick()
  agree1El = document.getElementById('agree1')
  agree2El = document.getElementById('agree2')
  canvasEl = document.getElementById('signPad')
  if (canvasEl) {
    initSignatureOn(canvasEl)
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(() => {
        cancelAnimationFrame(resizeRAF)
        resizeRAF = requestAnimationFrame(() => initSignatureOn(canvasEl))
      })
      ro.observe(canvasEl.parentElement || canvasEl)
    }
    window.addEventListener('resize', onResize, { passive: true })
  }
}
function onResize(){ if (!canvasEl) return; cancelAnimationFrame(resizeRAF); resizeRAF = requestAnimationFrame(()=>initSignatureOn(canvasEl)) }
function clearSign(){ lastStrokes = null; sig?.clear() }

async function submit(){
  if (isSubmitting.value) return
  const agree1 = !!agree1El?.checked, agree2 = !!agree2El?.checked
  if (!agree1 || !agree2) return alert('Bạn cần tích đủ 2 ô đồng ý trong hợp đồng.')
  if (!sig || sig.isEmpty()) return alert('Vui lòng ký vào khung chữ ký trong hợp đồng.')

  isSubmitting.value = true
  try {
    const signatureDataUrl = sig.toDataURL('image/png')
    // NEW: gửi stroke để BE lưu audit
    const signatureStrokes = (()=>{ try { return sig.toData() } catch { return undefined } })()
    const { data } = await api.post('/ctv/contract/sign', { signatureDataUrl, signatureStrokes, agree1, agree2 })
    if (data?.ok) {
      alert(`Ký thành công. Mã CTV: ${data.ctvCode}`)
      router.replace('/dashboard')
    } else throw new Error(data?.error || 'SIGN_FAILED')
  } catch (e) {
    alert('Gửi chữ ký thất bại: ' + (e?.response?.data?.error || e?.message || 'ERR'))
  } finally {
    isSubmitting.value = false
  }
}

onMounted(async () => { await fetchPreview(); await hydrateTemplate() })
onBeforeUnmount(() => {
  if (ro && canvasEl?.parentElement) { try { ro.unobserve(canvasEl.parentElement) } catch {} }
  ro = null
  window.removeEventListener('resize', onResize)
  try { sig?.off?.() } catch {}
  sig = null; canvasEl = agree1El = agree2El = null
})
</script>

<template>
  <div class="main scroll">
    <div class="contract-wrap">
      <div class="paper">
        <div v-html="htmlPreview" class="paper-body"></div>
      </div>
      <div class="actions">
        <button class="btn btn-outline" type="button" @click="clearSign">Xoá chữ ký</button>
        <button class="btn" type="button" :disabled="isSubmitting" @click="submit">
          {{ isSubmitting ? 'Đang gửi…' : 'Gửi hợp đồng' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.contract-wrap{
  width:min(980px,100%);
  margin: 8px auto 24px;
  background:#fff; color:#111827;
  border:1px solid #e5e7eb; border-radius:16px;
  padding:22px 20px 18px; box-shadow: 0 12px 28px rgba(0,0,0,.08);
}
.contract-title{ text-align:center; margin:6px 0 16px; color:#0f172a }
.paper{
  border:1px solid #e5e7eb; border-radius:12px; background:#fff;
  padding:28px 26px; box-shadow: 0 6px 18px rgba(2,6,23,.06);
}
.paper-body :deep(img){ max-width:100%; height:auto }
.actions{ margin-top:14px; display:flex; gap:12px; justify-content:space-between; align-items:center; }
.btn{ appearance:none; border:0; cursor:pointer; padding:10px 14px; border-radius:10px; background:var(--primary,#2dd4bf); color:#062b25; font-weight:600; box-shadow:0 8px 24px rgba(45,212,191,.25); }
.btn.btn-outline{ background:#fff; color:#0f172a; border:1px solid #e5e7eb; box-shadow:none; }
:global(.main.scroll){ position: static; inset: auto; display: block; overflow-y: auto; padding: 24px 12px; }
</style>
